import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import { profileAPI } from '../services/api';
import { ALL_PAGE_KEYS } from '../config/pages';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [role, setRole] = useState('viewer'); // least privilege by default
  // null = unrestricted (every page). An array limits the user to those page keys.
  // This is the EFFECTIVE set resolved server-side from the assigned role plus any
  // per-user override — the raw page_access column alone would miss the role's grants.
  const [pageAccess, setPageAccess] = useState(null);
  // Section keys the user's role hides. A deny list, so empty means hide nothing.
  const [hiddenSections, setHiddenSections] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadProfile = useCallback(async (user) => {
    if (!user) {
      setProfile(null);
      setRole('viewer');
      setPageAccess(null);
      setHiddenSections([]);
      return;
    }
    try {
      const p = await profileAPI.getByFirebaseUID(user.uid);
      setProfile(p);
      // Three tiers, mirroring backend utils/roles.py TIERS:
      //   super_admin — writes, plus the team/roles/settings consoles
      //   editor      — writes, but only where their pages allow; administers nothing
      //   viewer      — reads only
      // super_admin also honours the legacy is_admin flag so existing admins aren't
      // locked out before the role-normalization migration runs. Anything unrecognised
      // falls through to viewer (fail safe).
      const isSuper = !!p && (p.role === 'super_admin' || p.is_admin === true);
      const isEditor = !!p && p.role === 'editor';
      setRole(isSuper ? 'super_admin' : isEditor ? 'editor' : 'viewer');
      // effective_pages folds in the role; page_access is only the per-user override.
      setPageAccess(Array.isArray(p?.effective_pages) ? p.effective_pages : null);
      setHiddenSections(Array.isArray(p?.hidden_sections) ? p.hidden_sections : []);
    } catch (e) {
      // Profile not provisioned yet, or fetch failed -> least privilege.
      setProfile(null);
      setRole('viewer');
      setPageAccess(null);
      setHiddenSections([]);
    }
  }, []);

  useEffect(() => {
    if (!auth) {
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      await loadProfile(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [loadProfile]);

  const refreshProfile = useCallback(() => loadProfile(auth && auth.currentUser), [loadProfile]);

  const isSuperAdmin = role === 'super_admin';
  // Page restrictions apply to every role — a restricted super admin still can't see
  // the pages they were denied. The backend enforces the same rule on the API.
  const visiblePages = pageAccess === null ? ALL_PAGE_KEYS : pageAccess;
  const canView = useCallback(
    (pageKey) => (pageAccess === null ? true : pageAccess.includes(pageKey)),
    [pageAccess],
  );
  // Sections are a deny list — absent from hiddenSections means visible.
  const canViewSection = useCallback(
    (sectionKey) => !hiddenSections.includes(sectionKey),
    [hiddenSections],
  );

  const value = {
    currentUser,
    profile,
    role,
    isSuperAdmin,
    // isViewer means "read-only", NOT "not an admin" — an editor writes but is not a
    // super admin, and the old `!isSuperAdmin` definition would have made every editor
    // read-only in the UI while the API happily accepted their writes.
    isViewer: !!currentUser && role === 'viewer',
    canEdit: role === 'super_admin' || role === 'editor',
    pageAccess,
    visiblePages,
    hiddenSections,
    canViewSection,
    isPageRestricted: pageAccess !== null,
    canView,
    loading,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
